import React, { useState } from "react";
import analyticsService from "../../../services/analyticsService";
import "./ValuationFundamentals.scss";

interface FundamentalMetric {
  metric: string;
  value: string | number;
  whyImportant: string;
  bullishSignal: string;
  bearishSignal: string;
  signal: "bullish" | "bearish" | "neutral";
}

interface ValuationFundamentalsData {
  metrics: FundamentalMetric[];
}

interface ValuationFundamentalsProps {
  companyId?: number;
  company?: any;
  analytics?: any;
  data?: ValuationFundamentalsData;
  onRefresh?: () => void;
}

const ValuationFundamentals: React.FC<ValuationFundamentalsProps> = ({ companyId, company, analytics, data, onRefresh }) => {
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = () => {
    if (!companyId) return;
    setRefreshing(true);
    analyticsService.refreshFundamentals(companyId).subscribe({
      next: () => {
        if (onRefresh) onRefresh();
        setRefreshing(false);
      },
      error: (err) => {
        console.error("Failed to refresh fundamentals:", err);
        setRefreshing(false);
      }
    });
  };
  // Mock data - will be replaced with API data later
  const mockData: ValuationFundamentalsData = {
    metrics: [
      {
        metric: "P/E Ratio (TTM)",
        value: 18.5,
        whyImportant: "Earnings valuation",
        bullishSignal: "< Industry Avg",
        bearishSignal: "> Industry Avg",
        signal: "bullish"
      },
      {
        metric: "P/B Ratio",
        value: 3.2,
        whyImportant: "Asset vs price",
        bullishSignal: "<2 (undervalued)",
        bearishSignal: ">5 (overvalued)",
        signal: "neutral"
      },
      {
        metric: "EPS (TTM) & Growth",
        value: "₹45.30 (+12%)",
        whyImportant: "Profitability trend",
        bullishSignal: "Positive growth",
        bearishSignal: "Declining",
        signal: "bullish"
      },
      {
        metric: "Revenue Growth (YoY)",
        value: "18.5%",
        whyImportant: "Business expansion",
        bullishSignal: ">15% YoY",
        bearishSignal: "Negative",
        signal: "bullish"
      },
      {
        metric: "Debt/Equity",
        value: 0.65,
        whyImportant: "Financial risk",
        bullishSignal: "<0.5",
        bearishSignal: ">1",
        signal: "neutral"
      },
      {
        metric: "ROE / ROCE",
        value: "22% / 19%",
        whyImportant: "Efficiency",
        bullishSignal: ">20%",
        bearishSignal: "<10%",
        signal: "bullish"
      },
      {
        metric: "Order Book",
        value: "₹34,500 Cr",
        whyImportant: "Future revenue visibility",
        bullishSignal: ">3x Annual Revenue",
        bearishSignal: "Declining",
        signal: "bullish"
      }
    ]
  };

  // Transform analytics data to component format
  const getDataFromAnalytics = (): ValuationFundamentalsData => {
    if (!analytics || !analytics.financials) {
      return mockData;
    }

    const fin = analytics.financials;
    const metrics: FundamentalMetric[] = [];

    // P/E Ratio
    if (fin.pe_ratio !== null && fin.pe_ratio !== undefined) {
      metrics.push({
        metric: "P/E Ratio (TTM)",
        value: fin.pe_ratio.toFixed(2),
        whyImportant: "Earnings valuation",
        bullishSignal: "< Industry Avg",
        bearishSignal: "> Industry Avg",
        signal: fin.pe_signal || "neutral"
      });
    }

    // P/B Ratio
    if (fin.pb_ratio !== null && fin.pb_ratio !== undefined) {
      metrics.push({
        metric: "P/B Ratio",
        value: fin.pb_ratio.toFixed(2),
        whyImportant: "Asset vs price",
        bullishSignal: "<2 (undervalued)",
        bearishSignal: ">5 (overvalued)",
        signal: fin.pb_signal || "neutral"
      });
    }

    // EPS & Growth
    if (fin.eps !== null && fin.eps !== undefined) {
      const epsValue = fin.eps_growth
        ? `₹${fin.eps.toFixed(2)} (${fin.eps_growth > 0 ? '+' : ''}${fin.eps_growth.toFixed(1)}%)`
        : `₹${fin.eps.toFixed(2)}`;
      metrics.push({
        metric: "EPS (TTM) & Growth",
        value: epsValue,
        whyImportant: "Profitability trend",
        bullishSignal: "Positive growth",
        bearishSignal: "Declining",
        signal: fin.eps_signal || "neutral"
      });
    }

    // Revenue Growth
    if (fin.revenue_growth_yoy !== null && fin.revenue_growth_yoy !== undefined) {
      metrics.push({
        metric: "Revenue Growth (YoY)",
        value: `${fin.revenue_growth_yoy > 0 ? '+' : ''}${fin.revenue_growth_yoy.toFixed(1)}%`,
        whyImportant: "Business expansion",
        bullishSignal: ">15% YoY",
        bearishSignal: "Negative",
        signal: fin.revenue_signal || "neutral"
      });
    }

    // Debt/Equity
    if (fin.debt_to_equity !== null && fin.debt_to_equity !== undefined) {
      metrics.push({
        metric: "Debt/Equity",
        value: fin.debt_to_equity.toFixed(2),
        whyImportant: "Financial risk",
        bullishSignal: "<0.5",
        bearishSignal: ">1",
        signal: fin.debt_signal || "neutral"
      });
    }

    // ROE / ROCE
    if ((fin.roe !== null && fin.roe !== undefined) || (fin.roce !== null && fin.roce !== undefined)) {
      const roeStr = fin.roe ? `${fin.roe.toFixed(1)}%` : 'N/A';
      const roceStr = fin.roce ? `${fin.roce.toFixed(1)}%` : 'N/A';
      metrics.push({
        metric: "ROE / ROCE",
        value: `${roeStr} / ${roceStr}`,
        whyImportant: "Efficiency",
        bullishSignal: ">20%",
        bearishSignal: "<10%",
        signal: fin.profitability_signal || "neutral"
      });
    }

    // Order Book (if available)
    if (fin.order_book !== null && fin.order_book !== undefined) {
      metrics.push({
        metric: "Order Book",
        value: `₹${(fin.order_book / 10000).toFixed(0)} Cr`,
        whyImportant: "Future revenue visibility",
        bullishSignal: ">3x Annual Revenue",
        bearishSignal: "Declining",
        signal: "neutral"
      });
    }

    // If no metrics available from backend, use mock data
    return metrics.length > 0 ? { metrics } : mockData;
  };

  const fundamentalsData = data || getDataFromAnalytics();

  const getSignalIcon = (signal: string) => {
    switch (signal) {
      case "bullish":
        return <span className="icon has-text-success"><i className="fas fa-arrow-up"></i></span>;
      case "bearish":
        return <span className="icon has-text-danger"><i className="fas fa-arrow-down"></i></span>;
      case "neutral":
        return <span className="icon has-text-warning"><i className="fas fa-minus"></i></span>;
      default:
        return null;
    }
  };

  const getSignalTag = (signal: string) => {
    switch (signal) {
      case "bullish":
        return <span className="tag is-success is-light">Bullish</span>;
      case "bearish":
        return <span className="tag is-danger is-light">Bearish</span>;
      case "neutral":
        return <span className="tag is-warning is-light">Neutral</span>;
      default:
        return null;
    }
  };

  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="valuation-fundamentals mb-2">
      <div className="box py-3 px-3 mb-0">
        <div className="level mb-2">
          <div className="level-left">
            <div className="level-item">
              <h2 className="title is-6 mb-0">Valuation & Fundamentals</h2>
            </div>
          </div>
          <div className="level-right">
            <div className="level-item">
              <button
                className={`button is-small is-info is-outlined mr-2 ${refreshing ? 'is-loading' : ''}`}
                onClick={handleRefresh}
                disabled={refreshing}
                title="Refresh"
              >
                <span className="icon is-small">
                  <i className="fas fa-sync-alt"></i>
                </span>
              </button>
              <button
                className="button is-small is-primary is-outlined"
                onClick={() => setShowDetails(!showDetails)}
              >
                <span className="icon is-small">
                  <i className={`fas fa-${showDetails ? 'eye-slash' : 'info-circle'}`}></i>
                </span>
                <span>{showDetails ? 'Hide' : 'Details'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Compact Summary */}
        <div className="columns is-mobile mb-2" style={{ fontSize: '0.8rem' }}>
          <div className="column has-text-centered py-2">
            <p className="heading is-size-7 mb-1">Bullish</p>
            <p className="title is-6 has-text-success mb-0">
              {fundamentalsData.metrics.filter(m => m.signal === "bullish").length}
            </p>
          </div>
          <div className="column has-text-centered py-2">
            <p className="heading is-size-7 mb-1">Neutral</p>
            <p className="title is-6 has-text-warning mb-0">
              {fundamentalsData.metrics.filter(m => m.signal === "neutral").length}
            </p>
          </div>
          <div className="column has-text-centered py-2">
            <p className="heading is-size-7 mb-1">Bearish</p>
            <p className="title is-6 has-text-danger mb-0">
              {fundamentalsData.metrics.filter(m => m.signal === "bearish").length}
            </p>
          </div>
        </div>

        {/* Compact Table */}
        <div className="table-container">
          <table className="table is-fullwidth is-hoverable is-narrow" style={{ fontSize: '0.75rem' }}>
            <thead>
              <tr className="is-size-7">
                <th>Metric</th>
                <th>Value</th>
                <th className="has-text-centered">Status</th>
                {showDetails && (
                  <>
                    <th>Bullish If</th>
                    <th>Bearish If</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {fundamentalsData.metrics.map((item, index) => (
                <tr key={index}>
                  <td>
                    <span className="has-text-weight-semibold">{item.metric}</span>
                    {showDetails && (
                      <>
                        <br />
                        <span className="has-text-grey is-size-7">{item.whyImportant}</span>
                      </>
                    )}
                  </td>
                  <td>
                    <span className="has-text-weight-bold">{item.value}</span>
                  </td>
                  <td className="has-text-centered">
                    {getSignalTag(item.signal)}
                  </td>
                  {showDetails && (
                    <>
                      <td className="has-text-success-dark is-size-7">{item.bullishSignal}</td>
                      <td className="has-text-danger-dark is-size-7">{item.bearishSignal}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ValuationFundamentals;
